import { Injectable } from '@angular/core'
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http'
import { Observable } from 'rxjs'
import { tap, finalize } from 'rxjs/operators'
import { LoaderService } from './loader/loader.service'
import { NotificationService } from './notification/notification.service'
import { IGNORE_GLOBAL_LOADER_APIS } from '../shared-components/constants'
import { ERROR_MESSAGES } from '../constants/toast-messages'

@Injectable({
  providedIn: 'root',
})
export class HttpInterceptorService implements HttpInterceptor {
  private trackedRequestsCount = 0

  constructor(
    private loaderService: LoaderService,
    private notificationService: NotificationService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let shouldTrackLoader = false

    // Check if this is a polling request
    const isPollingRequest = request.url.includes('polling=true')

    if (
      !isPollingRequest &&
      IGNORE_GLOBAL_LOADER_APIS.every((url) => request.url.indexOf(url) === -1)
    ) {
      this.showLoader()
      shouldTrackLoader = true
    }

    return next.handle(request).pipe(
      tap(
        () => {
          // Handle successful responses - no action needed
        },
        (err: any) => {
          // Handle errors only
          if (err.status == 401) {
            sessionStorage.removeItem('api_token')
            window.location.href = JSON.parse(
              localStorage.getItem('baseUrl')
            ).sso.url
          } else if (err.status == 422) {
            // Loader will be handled by finalize()
          } else if (err.status == 413) {
            this.notificationService.errorHandler(
              ERROR_MESSAGES.FILE_SIZE_LIMIT_EXCEEDED
            )
          } else if (err.status == 404) {
            if (err.error instanceof Blob) {
              const reader = new FileReader()
              reader.onload = () => {
                try {
                  const errorContent = JSON.parse(reader.result as string)
                  this.notificationService.errorHandler(
                    errorContent?.message || ERROR_MESSAGES.RESOURCE_NOT_FOUND
                  )
                } catch {
                  this.notificationService.errorHandler(
                    ERROR_MESSAGES.RESOURCE_NOT_FOUND
                  )
                }
              }
              reader.readAsText(err.error)
            } else {
              const errorContent = err.error
              this.notificationService.errorHandler(
                errorContent?.message || ERROR_MESSAGES.RESOURCE_NOT_FOUND
              )
            }
          } else if (this.inRange(err.status, 399, 599)) {
            this.notificationService.errorHandler(
              err.error.message || ERROR_MESSAGES.SOMETHING_WENT_WRONG
            )
          } else {
            this.notificationService.errorHandler(
              ERROR_MESSAGES.SOMETHING_WENT_WRONG_TRY_AGAIN
            )
          }
        }
      ),
      finalize(() => {
        if (shouldTrackLoader) {
          this.hideLoader()
        }
      })
    )
  }

  inRange(x: number, min: number, max: number): boolean {
    return (x - min) * (x - max) <= 0
  }

  private hideLoader(): void {
    this.trackedRequestsCount--

    if (this.trackedRequestsCount <= 0) {
      this.trackedRequestsCount = 0
      this.loaderService.loaderSubject.next(false)
    }
  }

  private showLoader(): void {
    this.trackedRequestsCount++

    // Only show loader on first tracked request
    if (this.trackedRequestsCount === 1) {
      this.loaderService.loaderSubject.next(true)
    }
  }
}
